import {
  ReactNode,
  RefObject,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { View, useWindowDimensions } from 'react-native';
import { Observable, Subject, auditTime, startWith } from 'rxjs';

/**
 * Moments a card may have come into view: the dashboard scrolled, or its
 * content was laid out again. Null outside a dashboard (the card page, a
 * chat draft), where a card is always in view.
 */
const BiViewportContext = createContext<Observable<void> | null>(null);

/** The scroll view's half of the viewport: what it reports, to cards. */
export class BiViewportTicks {
  /** Frequent events reach cards at most every 100 ms. */
  readonly changes: Observable<void>;
  private readonly ticks = new Subject<void>();

  constructor() {
    this.changes = this.ticks.pipe(auditTime(100), startWith(undefined));
  }

  notify = () => this.ticks.next();
}

export const BiViewportProvider = memo(
  ({ ticks, children }: { ticks: BiViewportTicks; children: ReactNode }) => (
    <BiViewportContext.Provider value={ticks.changes}>
      {children}
    </BiViewportContext.Provider>
  ),
);

/** How far below (or above) the screen a card starts loading. */
const kLookAheadScreens = 0.5;

/**
 * Whether the view has come within reach of the screen yet; once true it
 * stays true, so a card read once keeps its data as it scrolls away. Pass
 * `onLayout` to the measured view: a card placed after the last scroll is
 * checked when it lands.
 */
export function useBiSeen(ref: RefObject<View>): {
  seen: boolean;
  onLayout: () => void;
} {
  const changes = useContext(BiViewportContext);
  const { height } = useWindowDimensions();
  const [seen, setSeen] = useState(changes === null);
  const check = useCallback(() => {
    const margin = height * kLookAheadScreens;
    ref.current?.measureInWindow((_x, y, _width, viewHeight) => {
      // An unmeasured view reads 0 × 0 at the origin: not yet placed.
      if (!viewHeight) return;
      if (y < height + margin && y + viewHeight > -margin) setSeen(true);
    });
  }, [height, ref]);
  useEffect(() => {
    if (seen || !changes) return;
    const subscription = changes.subscribe(check);
    return () => subscription.unsubscribe();
  }, [seen, changes, check]);
  return { seen, onLayout: seen ? noop : check };
}

const noop = () => undefined;
